import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import MaintenanceScreen from "@/components/MaintenanceScreen";
import PrelaunchScreen from "@/components/PrelaunchScreen";
import SocialStrip from "@/components/SocialStrip";
import { LogIn, LogOut } from "lucide-react";

const navItems = [
  { to: "/", label: "Start", testid: "nav-home", end: true },
  { to: "/rennberichte", label: "Rennberichte", testid: "nav-races" },
  { to: "/news", label: "News", testid: "nav-news" },
  { to: "/fahrer", label: "Fahrer", testid: "nav-drivers" },
  { to: "/kalender", label: "Kalender", testid: "nav-calendar" },
  { to: "/wertung", label: "Wertung", testid: "nav-standings" },
  { to: "/roadmap", label: "Roadmap", testid: "nav-roadmap" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = user && user.role === "admin";
  const { settings, maintenance_active, prelaunch_active } = useSiteSettings();
  const logoSrc = settings.logo_url || "/brand/sector-four.png";

  // Bypass-Routen: Admin-Bereich, Login + rechtliche Pflichtseiten bleiben IMMER erreichbar.
  // Eingeloggte Admins sehen alles ohne Beschränkung.
  const isLegal = loc.pathname.startsWith("/impressum") || loc.pathname.startsWith("/datenschutz");
  const bypass =
    isAdmin ||
    loc.pathname.startsWith("/admin") ||
    loc.pathname.startsWith("/login") ||
    isLegal;
  if (prelaunch_active && !bypass) {
    return (
      <PrelaunchScreen
        eyebrow={settings.prelaunch_eyebrow}
        title={settings.prelaunch_title}
        message={settings.prelaunch_message}
        target={settings.prelaunch_target}
        themeColor={settings.theme_color || "#00E5FF"}
      />
    );
  }
  if (maintenance_active && !bypass) {
    return <MaintenanceScreen message={settings.maintenance_message} />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#0B0F19] text-white">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0B0F19]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo">
            <img
              src={logoSrc}
              alt={settings.site_title || "Sector Four"}
              className="h-16 w-auto"
              draggable={false}
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `font-display font-bold uppercase tracking-wide text-sm transition-colors ${
                    isActive ? "text-[#00E5FF]" : "text-white/80 hover:text-white"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {isAdmin ? (
              <>
                <NavLink to="/admin" className="btn-ghost text-xs hidden sm:inline-flex" data-testid="nav-admin">
                  Admin
                </NavLink>
                <button onClick={async () => { await logout(); nav("/"); }} className="btn-ghost text-xs inline-flex items-center" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </button>
              </>
            ) : (
              <Link to="/login" className="btn-ghost text-xs inline-flex items-center" data-testid="nav-login">
                <LogIn className="w-4 h-4 mr-2" /> Admin
              </Link>
            )}
          </div>
        </div>
        <div className="md:hidden border-t border-white/5 px-6 py-2 flex gap-5 overflow-x-auto">
          {navItems.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end}
              data-testid={`mobile-${it.testid}`}
              className={({ isActive }) =>
                `font-display font-bold uppercase tracking-wide text-xs whitespace-nowrap ${isActive ? "text-[#00E5FF]" : "text-white/70"}`
              }>{it.label}</NavLink>
          ))}
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-white/5 mt-24 py-12 bg-[#0B0F19]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-4 gap-10">
          <div data-testid="footer-brand">
            <img src={logoSrc} alt={settings.site_title || "Sector Four"} className="h-16 w-auto" draggable={false} />
            <p className="text-neutral-400 mt-4 text-sm max-w-xs whitespace-pre-line" data-testid="footer-tagline">
              {settings.footer_tagline}
            </p>
            <div className="mt-4">
              <SocialStrip settings={settings} />
            </div>
          </div>
          <div>
            <div className="eyebrow">Navigation</div>
            <ul className="mt-4 space-y-2 text-sm text-neutral-300">
              <li><Link to="/rennberichte" className="hover:text-[#00E5FF]">Rennberichte</Link></li>
              <li><Link to="/news" className="hover:text-[#00E5FF]">News</Link></li>
              <li><Link to="/fahrer" className="hover:text-[#00E5FF]">Fahrer</Link></li>
              <li><Link to="/kalender" className="hover:text-[#00E5FF]">Kalender</Link></li>
              <li><Link to="/wertung" className="hover:text-[#00E5FF]">Wertung</Link></li>
              <li><Link to="/roadmap" className="hover:text-[#00E5FF]">Roadmap</Link></li>
            </ul>
          </div>
          <div data-testid="footer-legal">
            <div className="eyebrow">Rechtliches</div>
            <ul className="mt-4 space-y-2 text-sm text-neutral-300">
              <li><Link to="/impressum" className="hover:text-[#00E5FF]" data-testid="footer-link-impressum">Impressum</Link></li>
              <li><Link to="/datenschutz" className="hover:text-[#00E5FF]" data-testid="footer-link-datenschutz">Datenschutz</Link></li>
            </ul>
          </div>
          <div data-testid="footer-liga">
            <div className="eyebrow">Liga</div>
            <p className="text-neutral-400 mt-4 text-sm whitespace-pre-line" data-testid="footer-liga-text">
              {settings.footer_liga_text}
            </p>
            <p className="text-neutral-600 mt-6 text-xs">© {new Date().getFullYear()} Sector Four · Motorsport, Unfiltered</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
